import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { adminService, userService } from '../../services/api';
import '../../styles/Admin.css';

const AdminUsers = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [completeDeleteModalOpen, setCompleteDeleteModalOpen] = useState(false);
  const [userToCompleteDelete, setUserToCompleteDelete] = useState(null);
  const [actionInProgress, setActionInProgress] = useState(false);
  const [actionMessage, setActionMessage] = useState({ type: '', text: '' });

  // Vérifier si l'utilisateur est admin
  useEffect(() => {
    if (!currentUser || !currentUser.isAdmin) {
      navigate('/');
    }
  }, [currentUser, navigate]);

  // Charger la liste des utilisateurs
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await adminService.getAllUsers();
        setUsers(data);
      } catch (err) {
        console.error('Erreur lors du chargement des utilisateurs:', err);
        setError('Impossible de charger la liste des utilisateurs. Veuillez réessayer plus tard.');
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  // Fonction pour ouvrir la modal de suppression standard
  const openDeleteModal = (user) => {
    setUserToDelete(user);
    setDeleteModalOpen(true);
  };

  // Fonction pour ouvrir la modal de suppression complète (Loi 25)
  const openCompleteDeleteModal = (user) => {
    setUserToCompleteDelete(user);
    setCompleteDeleteModalOpen(true);
  };

  // Fonction pour fermer les modals
  const closeModals = () => {
    setDeleteModalOpen(false);
    setCompleteDeleteModalOpen(false);
    setUserToDelete(null);
    setUserToCompleteDelete(null);
  };

  // Fonction pour supprimer un utilisateur (suppression standard)
  const handleDeleteUser = async () => {
    if (!userToDelete) return;

    try {
      setActionInProgress(true);
      await adminService.deleteUser(userToDelete._id);
      
      // Mettre à jour la liste des utilisateurs
      setUsers(users.filter(user => user._id !== userToDelete._id));
      
      // Fermer la modal immédiatement
      closeModals();
      
      // Afficher le message de succès
      setActionMessage({
        type: 'success',
        text: `L'utilisateur ${userToDelete.firstName} ${userToDelete.lastName} a été supprimé avec succès.`
      });
      
      // Effacer le message après 3 secondes
      setTimeout(() => {
        setActionMessage({ type: '', text: '' });
      }, 3000);
    } catch (err) {
      console.error('Erreur lors de la suppression de l\'utilisateur:', err);
      setActionMessage({
        type: 'error',
        text: 'Une erreur est survenue lors de la suppression de l\'utilisateur. Veuillez réessayer plus tard.'
      });
      setActionInProgress(false);
    } finally {
      setActionInProgress(false);
    }
  };

  // Fonction pour basculer le statut administrateur d'un utilisateur
  const toggleAdminStatus = async (userId, currentStatus) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir ${currentStatus ? 'retirer les droits administrateur' : 'donner les droits administrateur'} à cet utilisateur ?`)) {
      return;
    }

    try {
      setActionInProgress(true);
      await adminService.updateUserRole(userId, !currentStatus);
      
      // Mettre à jour la liste des utilisateurs
      setUsers(users.map(user => 
        user._id === userId ? { ...user, isAdmin: !currentStatus } : user
      ));
      
      setActionMessage({
        type: 'success',
        text: `Les droits administrateur ont été ${currentStatus ? 'retirés' : 'ajoutés'} avec succès.`
      });
    } catch (err) {
      console.error('Erreur lors de la mise à jour des droits administrateur:', err);
      setActionMessage({
        type: 'error',
        text: 'Une erreur est survenue lors de la mise à jour des droits administrateur.'
      });
    } finally {
      setActionInProgress(false);
      
      // Effacer le message après 3 secondes
      setTimeout(() => {
        setActionMessage({ type: '', text: '' });
      }, 3000);
    }
  };

  // Fonction pour supprimer complètement les données d'un utilisateur (conformité Loi 25)
  const handleCompleteDeleteUser = async () => {
    if (!userToCompleteDelete) return;

    try {
      setActionInProgress(true);
      await adminService.deleteUserCompleteData(userToCompleteDelete._id);
      
      // Mettre à jour la liste des utilisateurs
      setUsers(users.filter(user => user._id !== userToCompleteDelete._id));
      
      // Fermer la modal immédiatement
      closeModals();
      
      // Afficher le message de succès
      setActionMessage({
        type: 'success',
        text: `Toutes les données de l'utilisateur ${userToCompleteDelete.firstName} ${userToCompleteDelete.lastName} ont été supprimées conformément à la Loi 25 du Québec.`
      });
      
      // Effacer le message après 3 secondes
      setTimeout(() => {
        setActionMessage({ type: '', text: '' });
      }, 3000);
    } catch (err) {
      console.error('Erreur lors de la suppression complète des données:', err);
      setActionMessage({
        type: 'error',
        text: 'Une erreur est survenue lors de la suppression complète des données. Veuillez réessayer plus tard.'
      });
      setActionInProgress(false);
    } finally {
      setActionInProgress(false);
    }
  };

  // Afficher un indicateur de chargement pendant le chargement des données
  if (loading) {
    return (
      <div className="admin-page">
        <div className="container">
          <h1 className="page-title">Gestion des utilisateurs</h1>
          <div className="loading-indicator">Chargement des utilisateurs...</div>
        </div>
      </div>
    );
  }

  // Afficher un message d'erreur si le chargement a échoué
  if (error) {
    return (
      <div className="admin-page">
        <div className="container">
          <h1 className="page-title">Gestion des utilisateurs</h1>
          <div className="error-message">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="container">
        <h1 className="page-title">Gestion des utilisateurs</h1>
        
        {actionMessage.text && (
          <div className={`alert ${actionMessage.type === 'success' ? 'alert-success' : 'alert-danger'}`}>
            {actionMessage.text}
          </div>
        )}
        
        <div className="admin-table-container">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Nom</th>
                <th>Prénom</th>
                <th>Date de création</th>
                <th>Admin</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user._id}>
                  <td>{user.lastName}</td>
                  <td>{user.firstName}</td>
                  <td>{new Date(user.createdAt).toLocaleDateString()}</td>
                  <td className="text-center">
                    <div className="form-check form-switch d-inline-block">
                      <input 
                        className="form-check-input" 
                        type="checkbox" 
                        role="switch"
                        checked={user.isAdmin || false}
                        onChange={() => toggleAdminStatus(user._id, user.isAdmin || false)}
                        disabled={actionInProgress || user._id === currentUser?._id}
                      />
                    </div>
                  </td>
                  <td className="actions-cell">
                    <button 
                      className="btn btn-sm btn-primary"
                      onClick={() => navigate(`/profile/${user._id}`)}
                      title="Voir le profil"
                    >
                      <i className="fas fa-eye"></i> Voir
                    </button>
                    <button 
                      className="btn btn-sm btn-danger ml-2"
                      onClick={() => openDeleteModal(user)}
                      disabled={user._id === currentUser?._id}
                      title="Supprimer l'utilisateur"
                    >
                      <i className="fas fa-trash"></i> Supprimer
                    </button>
                    <button 
                      className="btn btn-sm btn-warning ml-2"
                      onClick={() => openCompleteDeleteModal(user)}
                      title="Supprimer complètement toutes les données (Loi 25)"
                      disabled={user._id === currentUser?._id}
                    >
                      <i className="fas fa-trash-alt"></i> Loi 25
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {/* Modal de suppression standard */}
        {deleteModalOpen && (
          <div className="modal-overlay" style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000
          }}>
            <div className="modal-content" style={{
              backgroundColor: 'white',
              padding: '20px',
              borderRadius: '5px',
              maxWidth: '500px',
              width: '90%',
              boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)'
            }}>
              <h2 style={{ marginBottom: '15px' }}>Confirmer la suppression</h2>
              <p style={{ marginBottom: '20px' }}>
                Êtes-vous sûr de vouloir supprimer l'utilisateur {userToDelete?.firstName} {userToDelete?.lastName} ?
              </p>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <button 
                  className="btn btn-secondary"
                  onClick={closeModals}
                  disabled={actionInProgress}
                >
                  Annuler
                </button>
                <button 
                  className="btn btn-danger ml-2"
                  onClick={handleDeleteUser}
                  disabled={actionInProgress}
                >
                  {actionInProgress ? 'Suppression en cours...' : 'Supprimer'}
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Modal de suppression complète (Loi 25) */}
        {completeDeleteModalOpen && (
          <div className="modal-overlay" style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000
          }}>
            <div className="modal-content" style={{
              backgroundColor: 'white',
              padding: '20px',
              borderRadius: '5px',
              maxWidth: '500px',
              width: '90%',
              boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)'
            }}>
              <h2 style={{ color: '#dc3545', marginBottom: '15px' }}>Confirmer la suppression complète (Loi 25)</h2>
              <p style={{ marginBottom: '15px' }}>
                <strong>ATTENTION:</strong> Vous êtes sur le point de supprimer <strong>DÉFINITIVEMENT</strong> toutes les données 
                de l'utilisateur {userToCompleteDelete?.firstName} {userToCompleteDelete?.lastName}, y compris tous les fichiers 
                associés à son profil.
              </p>
              <p style={{ marginBottom: '15px' }}>
                Cette action est irréversible et est effectuée conformément à la Loi 25 du Québec sur la protection 
                des renseignements personnels.
              </p>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <button 
                  className="btn btn-secondary"
                  onClick={closeModals}
                  disabled={actionInProgress}
                >
                  Annuler
                </button>
                <button 
                  className="btn btn-danger ml-2"
                  onClick={handleCompleteDeleteUser}
                  disabled={actionInProgress}
                >
                  {actionInProgress ? 'Suppression en cours...' : 'Supprimer définitivement (Loi 25)'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminUsers;
